import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Users, EnvelopeSimple, Download, Calendar, 
  MagnifyingGlass, CaretDown, Eye, Trash, Export,
  ShieldCheck, Lock, ArrowRight, ChartLineUp,
  Clock, CheckCircle, Warning, Newspaper, PencilSimple,
  Plus, X, FloppyDisk, Article, Tag, Image,
  Target, Lightning, Fire, UserPlus, PaperPlaneTilt,
  Phone, Envelope, CalendarCheck, TrendUp
} from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple password protection (in production, use proper auth)
const ADMIN_PASSWORD = "noble2026";

const BLOG_CATEGORIES = [
  { id: "compliance", name: "Compliance & Regulations" },
  { id: "operations", name: "Operations & Management" },
  { id: "clinical", name: "Clinical Excellence" },
  { id: "technology", name: "Technology & AI" },
  { id: "leadership", name: "Leadership & Growth" },
  { id: "resources", name: "Resources & Guides" }
];

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  
  const [activeTab, setActiveTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadStats, setLeadStats] = useState(null);
  const [outreachDashboard, setOutreachDashboard] = useState(null);
  const [followUpTasks, setFollowUpTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [calendarStatus, setCalendarStatus] = useState({ configured: false, connected: false });
  const [stats, setStats] = useState({
    contacts: 0,
    bookings: 0,
    downloads: 0,
    subscribers: 0,
    blog: 0,
    leads: 0
  });
  
  // Lead Management State
  const [selectedLeadStage, setSelectedLeadStage] = useState("all");
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [followUpForm, setFollowUpForm] = useState({
    task_type: "call",
    due_date: "",
    notes: ""
  });

  // Blog Editor State
  const [showBlogEditor, setShowBlogEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "compliance",
    tags: "",
    author: "Noble Hospice Solutions Team",
    featured_image: "",
    meta_title: "",
    meta_description: "",
    seo_keywords: ""
  });
  const [savingPost, setSavingPost] = useState(false);

  useEffect(() => {
    // Check if already authenticated in session
    const authStatus = sessionStorage.getItem("adminAuth");
    if (authStatus === "true") {
      setIsAuthenticated(true);
    }
    
    // Check URL for calendar connection success
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('calendar') === 'connected') {
      window.history.replaceState({}, '', '/admin');
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
      checkCalendarStatus();
    }
  }, [isAuthenticated]);

  const checkCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API}/calendar/status`);
      setCalendarStatus(response.data);
    } catch (err) {
      console.log("Calendar status check failed:", err);
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const response = await axios.get(`${API}/calendar/auth/login`);
      window.location.href = response.data.authorization_url;
    } catch (err) {
      alert("Failed to start Google Calendar connection. Make sure credentials are configured.");
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem("adminAuth", "true");
      setPasswordError("");
    } else {
      setPasswordError("Invalid password. Please try again.");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("adminAuth");
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [contactsRes, bookingsRes, downloadsRes, subscribersRes, blogRes, leadsRes, leadStatsRes, outreachRes, followUpsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/downloads`),
        axios.get(`${API}/admin/subscribers`),
        axios.get(`${API}/blog/posts?status=all&limit=100`).catch(() => ({ data: { posts: [] } })),
        axios.get(`${API}/leads/hot?limit=100`).catch(() => ({ data: { leads: [] } })),
        axios.get(`${API}/leads/stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/outreach/dashboard`).catch(() => ({ data: {} })),
        axios.get(`${API}/outreach/follow-ups?status=all`).catch(() => ({ data: { tasks: [] } }))
      ]);
      
      setContacts(contactsRes.data);
      setBookings(bookingsRes.data);
      setDownloads(downloadsRes.data);
      setSubscribers(subscribersRes.data);
      setBlogPosts(blogRes.data.posts || []);
      setLeads(leadsRes.data.leads || []);
      setLeadStats(leadStatsRes.data);
      setOutreachDashboard(outreachRes.data);
      setFollowUpTasks(followUpsRes.data.tasks || []);
      
      setStats({
        contacts: contactsRes.data.length,
        bookings: bookingsRes.data.length,
        downloads: downloadsRes.data.length,
        subscribers: subscribersRes.data.length,
        blog: (blogRes.data.posts || []).length,
        leads: leadStatsRes.data.total_leads || 0
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) return;
    
    const headers = Object.keys(data[0]).filter(key => key !== '_id');
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleBlogFormChange = (field, value) => {
    setBlogForm(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-generate slug from title
      if (field === 'title' && !editingPost) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const openNewBlogPost = () => {
    setEditingPost(null);
    setBlogForm({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      category: "compliance",
      tags: "",
      author: "Noble Hospice Solutions Team",
      featured_image: "",
      meta_title: "",
      meta_description: "",
      seo_keywords: ""
    });
    setShowBlogEditor(true);
  };

  const openEditBlogPost = (post) => {
    setEditingPost(post);
    setBlogForm({
      title: post.title || "",
      slug: post.slug || "",
      excerpt: post.excerpt || "",
      content: post.content || "",
      category: post.category || "compliance",
      tags: Array.isArray(post.tags) ? post.tags.join(", ") : "",
      author: post.author || "Noble Hospice Solutions Team",
      featured_image: post.featured_image || "",
      meta_title: post.meta_title || "",
      meta_description: post.meta_description || "",
      seo_keywords: Array.isArray(post.seo_keywords) ? post.seo_keywords.join(", ") : ""
    });
    setShowBlogEditor(true);
  };

  const saveBlogPost = async (publish = false) => {
    setSavingPost(true);
    try {
      const postData = {
        title: blogForm.title,
        slug: blogForm.slug || generateSlug(blogForm.title),
        excerpt: blogForm.excerpt,
        content: blogForm.content,
        category: blogForm.category,
        tags: blogForm.tags.split(',').map(t => t.trim()).filter(Boolean),
        author: blogForm.author,
        featured_image: blogForm.featured_image,
        meta_title: blogForm.meta_title || blogForm.title,
        meta_description: blogForm.meta_description || blogForm.excerpt.substring(0, 160),
        seo_keywords: blogForm.seo_keywords.split(',').map(k => k.trim()).filter(Boolean)
      };

      if (editingPost) {
        await axios.put(`${API}/blog/posts/${editingPost.id}`, postData);
      } else {
        await axios.post(`${API}/blog/posts`, postData);
      }

      if (publish && !editingPost) {
        // Publish the post
        await axios.put(`${API}/blog/posts/${postData.slug}/publish`).catch(() => {});
      }

      alert(editingPost ? "Post updated successfully!" : "Post created successfully!");
      setShowBlogEditor(false);
      fetchAllData();
    } catch (error) {
      console.error("Error saving post:", error);
      alert("Failed to save post. Please try again.");
    } finally {
      setSavingPost(false);
    }
  };

  const deleteBlogPost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    
    try {
      await axios.delete(`${API}/blog/posts/${postId}`);
      alert("Post deleted successfully!");
      fetchAllData();
    } catch (error) {
      console.error("Error deleting post:", error);
      alert("Failed to delete post.");
    }
  };

  // Lead Management Functions
  const getStageColor = (stage) => {
    switch (stage) {
      case "hot": return "bg-red-100 text-red-700 border-red-200";
      case "warm": return "bg-orange-100 text-orange-700 border-orange-200";
      case "engaged": return "bg-blue-100 text-blue-700 border-blue-200";
      case "new": return "bg-slate-100 text-slate-700 border-slate-200";
      default: return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  const getStageIcon = (stage) => {
    switch (stage) {
      case "hot": return Fire;
      case "warm": return TrendUp;
      case "engaged": return Lightning;
      case "new": return UserPlus;
      default: return Users;
    }
  };

  const filterLeadsByStage = (leadsData) => {
    if (selectedLeadStage === "all") return leadsData;
    return leadsData.filter(lead => lead.stage === selectedLeadStage);
  };

  const createFollowUp = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;

    try {
      await axios.post(`${API}/outreach/follow-up`, {
        lead_email: selectedLead.email,
        task_type: followUpForm.task_type,
        due_date: followUpForm.due_date,
        notes: followUpForm.notes
      });
      alert("Follow-up task created!");
      setShowFollowUpModal(false);
      setFollowUpForm({ task_type: "call", due_date: "", notes: "" });
      fetchAllData();
    } catch (error) {
      console.error("Error creating follow-up:", error);
      alert("Failed to create follow-up task.");
    }
  };

  const completeFollowUp = async (taskId) => {
    try {
      await axios.put(`${API}/outreach/follow-ups/${taskId}/complete`);
      fetchAllData();
    } catch (error) {
      console.error("Error completing follow-up:", error);
    }
  };

  const enrollInSequence = async (email, sequenceId) => {
    try {
      await axios.post(`${API}/outreach/enroll-sequence`, {
        lead_email: email,
        sequence_id: sequenceId
      });
      alert(`Lead enrolled in ${sequenceId} sequence!`);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to enroll in sequence");
    }
  };

  const tabs = [
    { id: "contacts", label: "Contacts", icon: EnvelopeSimple, count: stats.contacts },
    { id: "leads", label: "Leads", icon: Target, count: stats.leads },
    { id: "bookings", label: "Demo Bookings", icon: Calendar, count: stats.bookings },
    { id: "downloads", label: "Downloads", icon: Download, count: stats.downloads },
    { id: "subscribers", label: "Newsletter", icon: Newspaper, count: stats.subscribers },
    { id: "blog", label: "Blog Posts", icon: Article, count: stats.blog }
  ];

  const filterData = (data) => {
    if (!searchTerm) return data;
    return data.filter(item => 
      Object.values(item).some(value => 
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock weight="duotone" className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-display text-2xl text-foreground mb-2">Admin Dashboard</h1>
            <p className="text-muted-foreground">Enter password to access</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-foreground mb-2">Password</label>
              <input
                type="password"
                className="form-input"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
              />
              {passwordError && (
                <p className="text-red-500 text-sm mt-2">{passwordError}</p>
              )}
            </div>
            <button
              type="submit"
              className="btn-primary w-full"
              data-testid="admin-login-btn"
            >
              <ShieldCheck weight="bold" className="w-5 h-5 mr-2" />
              Access Dashboard
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Blog Editor Modal
  if (showBlogEditor) {
    return (
      <div className="min-h-screen bg-muted/50 p-6" data-testid="blog-editor">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-6 text-white flex items-center justify-between">
              <div>
                <h1 className="font-display text-2xl font-bold">
                  {editingPost ? "Edit Blog Post" : "Create New Blog Post"}
                </h1>
                <p className="text-white/80 text-sm mt-1">Fill in the details below to {editingPost ? "update" : "create"} your post</p>
              </div>
              <button
                onClick={() => setShowBlogEditor(false)}
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <X weight="bold" className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Post Title *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter a compelling title..."
                  value={blogForm.title}
                  onChange={(e) => handleBlogFormChange('title', e.target.value)}
                  required
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">URL Slug</label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 text-sm">/blog/</span>
                  <input
                    type="text"
                    className="form-input flex-1"
                    placeholder="url-friendly-slug"
                    value={blogForm.slug}
                    onChange={(e) => handleBlogFormChange('slug', e.target.value)}
                  />
                </div>
              </div>

              {/* Category and Author */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">Category *</label>
                  <select
                    className="form-select"
                    value={blogForm.category}
                    onChange={(e) => handleBlogFormChange('category', e.target.value)}
                  >
                    {BLOG_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">Author</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Author name"
                    value={blogForm.author}
                    onChange={(e) => handleBlogFormChange('author', e.target.value)}
                  />
                </div>
              </div>

              {/* Featured Image */}
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Featured Image URL</label>
                <div className="flex items-center gap-3">
                  <Image weight="duotone" className="w-5 h-5 text-slate-400" />
                  <input
                    type="url"
                    className="form-input flex-1"
                    placeholder="https://images.unsplash.com/..."
                    value={blogForm.featured_image}
                    onChange={(e) => handleBlogFormChange('featured_image', e.target.value)}
                  />
                </div>
                {blogForm.featured_image && (
                  <img src={blogForm.featured_image} alt="Preview" className="mt-3 rounded-xl max-h-40 object-cover" />
                )}
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Excerpt *</label>
                <textarea
                  className="form-textarea"
                  placeholder="A brief summary of your post (shown in listings)..."
                  rows={3}
                  value={blogForm.excerpt}
                  onChange={(e) => handleBlogFormChange('excerpt', e.target.value)}
                  required
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Content * (HTML supported)</label>
                <textarea
                  className="form-textarea font-mono text-sm"
                  placeholder="<h2>Introduction</h2><p>Your content here...</p>"
                  rows={12}
                  value={blogForm.content}
                  onChange={(e) => handleBlogFormChange('content', e.target.value)}
                  required
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Tags (comma-separated)</label>
                <div className="flex items-center gap-3">
                  <Tag weight="duotone" className="w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    className="form-input flex-1"
                    placeholder="cms survey, compliance, hospice regulations"
                    value={blogForm.tags}
                    onChange={(e) => handleBlogFormChange('tags', e.target.value)}
                  />
                </div>
              </div>

              {/* SEO Section */}
              <div className="bg-slate-50 rounded-xl p-5 space-y-4">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <MagnifyingGlass weight="duotone" className="w-5 h-5 text-cyan-500" />
                  SEO Settings
                </h3>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Meta Title</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="SEO-optimized title (defaults to post title)"
                    value={blogForm.meta_title}
                    onChange={(e) => handleBlogFormChange('meta_title', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Meta Description</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Brief description for search results (max 160 chars)"
                    rows={2}
                    value={blogForm.meta_description}
                    onChange={(e) => handleBlogFormChange('meta_description', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">SEO Keywords (comma-separated)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="hospice compliance, cms survey preparation"
                    value={blogForm.seo_keywords}
                    onChange={(e) => handleBlogFormChange('seo_keywords', e.target.value)}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowBlogEditor(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={() => saveBlogPost(false)}
                  disabled={savingPost || !blogForm.title || !blogForm.excerpt || !blogForm.content}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <FloppyDisk weight="bold" className="w-5 h-5" />
                  {savingPost ? "Saving..." : "Save Draft"}
                </button>
                <button
                  onClick={() => saveBlogPost(true)}
                  disabled={savingPost || !blogForm.title || !blogForm.excerpt || !blogForm.content}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold px-6 py-3 rounded-full flex-1 flex items-center justify-center gap-2 hover:shadow-lg transition-all"
                >
                  <CheckCircle weight="bold" className="w-5 h-5" />
                  {savingPost ? "Publishing..." : "Save & Publish"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <ShieldCheck weight="fill" className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-foreground">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">Noble Hospice Solutions</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Google Calendar Connection */}
        <div className="bg-white rounded-xl p-4 border border-border mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              calendarStatus.connected ? "bg-green-100" : "bg-orange-100"
            }`}>
              <Calendar weight="duotone" className={`w-5 h-5 ${
                calendarStatus.connected ? "text-green-600" : "text-orange-600"
              }`} />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Google Calendar</h3>
              <p className="text-sm text-muted-foreground">
                {calendarStatus.connected 
                  ? "Connected - Demo bookings will sync to your calendar" 
                  : calendarStatus.configured 
                    ? "Not connected - Click to connect your calendar"
                    : "Not configured - Add Google credentials to .env"
                }
              </p>
            </div>
          </div>
          {calendarStatus.configured && !calendarStatus.connected && (
            <button
              onClick={connectGoogleCalendar}
              className="btn-primary text-sm"
              data-testid="connect-calendar-btn"
            >
              Connect Calendar
            </button>
          )}
          {calendarStatus.connected && (
            <span className="flex items-center gap-2 text-green-600 text-sm font-medium">
              <CheckCircle weight="fill" className="w-5 h-5" />
              Connected
            </span>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {tabs.map((tab) => (
            <motion.div
              key={tab.id}
              className={`bg-white rounded-xl p-6 border-2 cursor-pointer transition-all ${
                activeTab === tab.id ? "border-primary" : "border-transparent hover:border-primary/30"
              }`}
              onClick={() => setActiveTab(tab.id)}
              whileHover={{ scale: 1.02 }}
              data-testid={`stat-${tab.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  activeTab === tab.id ? "bg-primary text-white" : "bg-primary/10 text-primary"
                }`}>
                  <tab.icon weight="duotone" className="w-6 h-6" />
                </div>
                <span className="text-3xl font-bold text-foreground">{tab.count}</span>
              </div>
              <p className="text-sm font-medium text-muted-foreground">{tab.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search..."
              className="form-input pl-12"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="admin-search"
            />
          </div>
          {activeTab === "blog" && (
            <button
              onClick={openNewBlogPost}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2 hover:shadow-lg transition-all"
              data-testid="new-blog-post-btn"
            >
              <Plus weight="bold" className="w-5 h-5" />
              New Post
            </button>
          )}
          {activeTab !== "blog" && (
            <button
              onClick={() => {
                const dataMap = {
                  contacts: { data: contacts, name: "contacts" },
                  bookings: { data: bookings, name: "demo_bookings" },
                  downloads: { data: downloads, name: "resource_downloads" },
                  subscribers: { data: subscribers, name: "newsletter_subscribers" }
                };
                const { data, name } = dataMap[activeTab];
                exportToCSV(data, name);
              }}
              className="btn-secondary flex items-center gap-2"
              data-testid="export-csv"
            >
              <Export weight="bold" className="w-5 h-5" />
              Export CSV
            </button>
          )}
          <button
            onClick={fetchAllData}
            className="btn-primary flex items-center gap-2"
          >
            Refresh
          </button>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-xl border border-border overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading data...</p>
            </div>
          ) : (
            <>
              {/* Contacts Tab */}
              {activeTab === "contacts" && (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="contacts-table">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="text-left p-4 font-semibold text-foreground">Name</th>
                        <th className="text-left p-4 font-semibold text-foreground">Email</th>
                        <th className="text-left p-4 font-semibold text-foreground">Organization</th>
                        <th className="text-left p-4 font-semibold text-foreground">Interest</th>
                        <th className="text-left p-4 font-semibold text-foreground">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterData(contacts).length === 0 ? (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-muted-foreground">
                            No contacts found
                          </td>
                        </tr>
                      ) : (
                        filterData(contacts).map((contact, i) => (
                          <tr key={i} className="border-b border-border hover:bg-muted/30 transition-colors">
                            <td className="p-4 font-medium text-foreground">{contact.name}</td>
                            <td className="p-4 text-muted-foreground">{contact.email}</td>
                            <td className="p-4 text-muted-foreground">{contact.organization}</td>
                            <td className="p-4">
                              <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                                {contact.client_type}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">{formatDate(contact.created_at)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Bookings Tab */}
              {activeTab === "bookings" && (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="bookings-table">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="text-left p-4 font-semibold text-foreground">Name</th>
                        <th className="text-left p-4 font-semibold text-foreground">Email</th>
                        <th className="text-left p-4 font-semibold text-foreground">Organization</th>
                        <th className="text-left p-4 font-semibold text-foreground">Demo Type</th>
                        <th className="text-left p-4 font-semibold text-foreground">Scheduled For</th>
                        <th className="text-left p-4 font-semibold text-foreground">Booked On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterData(bookings).length === 0 ? (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-muted-foreground">
                            No bookings found
                          </td>
                        </tr>
                      ) : (
                        filterData(bookings).map((booking, i) => (
                          <tr key={i} className="border-b border-border hover:bg-muted/30 transition-colors">
                            <td className="p-4 font-medium text-foreground">{booking.name}</td>
                            <td className="p-4 text-muted-foreground">{booking.email}</td>
                            <td className="p-4 text-muted-foreground">{booking.organization}</td>
                            <td className="p-4">
                              <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full">
                                {booking.consultation_type}
                              </span>
                            </td>
                            <td className="p-4 text-foreground font-medium">
                              {booking.preferred_date} at {booking.preferred_time}
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">{formatDate(booking.created_at)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Downloads Tab */}
              {activeTab === "downloads" && (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="downloads-table">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="text-left p-4 font-semibold text-foreground">Name</th>
                        <th className="text-left p-4 font-semibold text-foreground">Email</th>
                        <th className="text-left p-4 font-semibold text-foreground">Organization</th>
                        <th className="text-left p-4 font-semibold text-foreground">Resource</th>
                        <th className="text-left p-4 font-semibold text-foreground">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterData(downloads).length === 0 ? (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-muted-foreground">
                            No downloads found
                          </td>
                        </tr>
                      ) : (
                        filterData(downloads).map((download, i) => (
                          <tr key={i} className="border-b border-border hover:bg-muted/30 transition-colors">
                            <td className="p-4 font-medium text-foreground">{download.name}</td>
                            <td className="p-4 text-muted-foreground">{download.email}</td>
                            <td className="p-4 text-muted-foreground">{download.organization}</td>
                            <td className="p-4">
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                {download.resource_title}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">{formatDate(download.downloaded_at)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Subscribers Tab */}
              {activeTab === "subscribers" && (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="subscribers-table">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="text-left p-4 font-semibold text-foreground">Email</th>
                        <th className="text-left p-4 font-semibold text-foreground">Name</th>
                        <th className="text-left p-4 font-semibold text-foreground">Organization</th>
                        <th className="text-left p-4 font-semibold text-foreground">Source</th>
                        <th className="text-left p-4 font-semibold text-foreground">Subscribed On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterData(subscribers).length === 0 ? (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-muted-foreground">
                            No subscribers found
                          </td>
                        </tr>
                      ) : (
                        filterData(subscribers).map((subscriber, i) => (
                          <tr key={i} className="border-b border-border hover:bg-muted/30 transition-colors">
                            <td className="p-4 font-medium text-foreground">{subscriber.email}</td>
                            <td className="p-4 text-muted-foreground">{subscriber.name || "-"}</td>
                            <td className="p-4 text-muted-foreground">{subscriber.organization || "-"}</td>
                            <td className="p-4">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                {subscriber.source || "Newsletter"}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">{formatDate(subscriber.subscribed_at)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Leads Tab */}
              {activeTab === "leads" && (
                <div data-testid="leads-tab">
                  {/* Lead Stats Overview */}
                  {leadStats && (
                    <div className="p-6 border-b border-border bg-gradient-to-r from-slate-50 to-white">
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                              <Fire weight="fill" className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-slate-900">{leadStats.by_stage?.hot || 0}</p>
                              <p className="text-xs text-slate-500 font-medium">Hot Leads</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                              <TrendUp weight="fill" className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-slate-900">{leadStats.by_stage?.warm || 0}</p>
                              <p className="text-xs text-slate-500 font-medium">Warm Leads</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Lightning weight="fill" className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-slate-900">{leadStats.by_stage?.engaged || 0}</p>
                              <p className="text-xs text-slate-500 font-medium">Engaged</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                              <UserPlus weight="fill" className="w-5 h-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-slate-900">{leadStats.by_stage?.new || 0}</p>
                              <p className="text-xs text-slate-500 font-medium">New Leads</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                              <Newspaper weight="fill" className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-slate-900">{leadStats.newsletter_subscribers || 0}</p>
                              <p className="text-xs text-slate-500 font-medium">Subscribers</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Outreach Metrics */}
                      {outreachDashboard?.metrics && (
                        <div className="mt-4 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl border border-cyan-200">
                          <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                            <PaperPlaneTilt weight="fill" className="w-5 h-5 text-cyan-600" />
                            Outreach Activity
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-lg font-bold text-cyan-600">{outreachDashboard.metrics.active_sequences}</p>
                              <p className="text-xs text-slate-600">Active Sequences</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-cyan-600">{outreachDashboard.metrics.emails_this_week}</p>
                              <p className="text-xs text-slate-600">Emails This Week</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-cyan-600">{outreachDashboard.metrics.pending_follow_ups}</p>
                              <p className="text-xs text-slate-600">Pending Follow-ups</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-amber-600">{outreachDashboard.metrics.overdue_follow_ups}</p>
                              <p className="text-xs text-slate-600">Overdue Tasks</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Stage Filter */}
                  <div className="p-4 border-b border-border flex items-center gap-2 overflow-x-auto">
                    <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">Filter by stage:</span>
                    {["all", "hot", "warm", "engaged", "new"].map((stage) => (
                      <button
                        key={stage}
                        onClick={() => setSelectedLeadStage(stage)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                          selectedLeadStage === stage
                            ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {stage === "all" ? "All Leads" : stage.charAt(0).toUpperCase() + stage.slice(1)}
                      </button>
                    ))}
                  </div>

                  {/* Leads Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="leads-table">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="text-left p-4 font-semibold text-foreground">Lead</th>
                          <th className="text-left p-4 font-semibold text-foreground">Organization</th>
                          <th className="text-left p-4 font-semibold text-foreground">Stage</th>
                          <th className="text-left p-4 font-semibold text-foreground">Score</th>
                          <th className="text-left p-4 font-semibold text-foreground">Source</th>
                          <th className="text-left p-4 font-semibold text-foreground">Last Activity</th>
                          <th className="text-left p-4 font-semibold text-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filterLeadsByStage(filterData(leads)).length === 0 ? (
                          <tr>
                            <td colSpan="7" className="p-8 text-center text-muted-foreground">
                              No leads found. Leads are captured automatically from forms, newsletter signups, and resource downloads.
                            </td>
                          </tr>
                        ) : (
                          filterLeadsByStage(filterData(leads)).map((lead, i) => {
                            const StageIcon = getStageIcon(lead.stage);
                            return (
                              <tr key={i} className="border-b border-border hover:bg-muted/30 transition-colors">
                                <td className="p-4">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStageColor(lead.stage)}`}>
                                      <StageIcon weight="fill" className="w-5 h-5" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-foreground">{lead.name || "Unknown"}</p>
                                      <p className="text-xs text-muted-foreground">{lead.email}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-4 text-muted-foreground">{lead.organization || "-"}</td>
                                <td className="p-4">
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStageColor(lead.stage)}`}>
                                    {lead.stage?.toUpperCase()}
                                  </span>
                                </td>
                                <td className="p-4">
                                  <div className="flex items-center gap-2">
                                    <div className="w-16 bg-slate-200 rounded-full h-2">
                                      <div
                                        className={`h-2 rounded-full ${
                                          lead.score >= 70 ? "bg-red-500" :
                                          lead.score >= 40 ? "bg-orange-500" :
                                          lead.score >= 20 ? "bg-blue-500" : "bg-slate-400"
                                        }`}
                                        style={{ width: `${lead.score || 0}%` }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold text-slate-700">{lead.score || 0}</span>
                                  </div>
                                </td>
                                <td className="p-4">
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                                    {lead.source || "website"}
                                  </span>
                                </td>
                                <td className="p-4 text-sm text-muted-foreground">
                                  {lead.last_activity ? formatDate(lead.last_activity) : "-"}
                                </td>
                                <td className="p-4">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => {
                                        setSelectedLead(lead);
                                        setShowFollowUpModal(true);
                                      }}
                                      className="w-8 h-8 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center hover:bg-cyan-200 transition-colors"
                                      title="Create Follow-up"
                                    >
                                      <CalendarCheck weight="bold" className="w-4 h-4" />
                                    </button>
                                    <div className="relative group">
                                      <button
                                        className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center hover:bg-purple-200 transition-colors"
                                        title="Enroll in Sequence"
                                      >
                                        <PaperPlaneTilt weight="bold" className="w-4 h-4" />
                                      </button>
                                      <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[160px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                        {["welcome", "nurture", "hot_lead", "re_engagement"].map((seq) => (
                                          <button
                                            key={seq}
                                            onClick={() => enrollInSequence(lead.email, seq)}
                                            className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                          >
                                            {seq.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                    {lead.phone && (
                                      <a
                                        href={`tel:${lead.phone}`}
                                        className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center hover:bg-emerald-200 transition-colors"
                                        title="Call Lead"
                                      >
                                        <Phone weight="bold" className="w-4 h-4" />
                                      </a>
                                    )}
                                    <a
                                      href={`mailto:${lead.email}`}
                                      className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center hover:bg-blue-200 transition-colors"
                                      title="Email Lead"
                                    >
                                      <Envelope weight="bold" className="w-4 h-4" />
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Follow-up Tasks Section */}
                  {followUpTasks.length > 0 && (
                    <div className="p-6 border-t border-border bg-slate-50">
                      <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <CalendarCheck weight="fill" className="w-5 h-5 text-cyan-600" />
                        Follow-up Tasks ({followUpTasks.filter(t => t.status === "pending").length} pending)
                      </h3>
                      <div className="space-y-3">
                        {followUpTasks.filter(t => t.status === "pending").slice(0, 5).map((task) => (
                          <div key={task.id} className="flex items-center justify-between bg-white rounded-xl p-4 border border-slate-200">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                task.task_type === "call" ? "bg-emerald-100" :
                                task.task_type === "email" ? "bg-blue-100" : "bg-purple-100"
                              }`}>
                                {task.task_type === "call" ? (
                                  <Phone weight="fill" className="w-5 h-5 text-emerald-600" />
                                ) : task.task_type === "email" ? (
                                  <Envelope weight="fill" className="w-5 h-5 text-blue-600" />
                                ) : (
                                  <Calendar weight="fill" className="w-5 h-5 text-purple-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{task.task_type.charAt(0).toUpperCase() + task.task_type.slice(1)} - {task.lead_name || task.lead_email}</p>
                                <p className="text-sm text-slate-500">Due: {formatDate(task.due_date)} {task.notes && `• ${task.notes}`}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => completeFollowUp(task.id)}
                              className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg font-semibold text-sm hover:bg-emerald-200 transition-colors flex items-center gap-2"
                            >
                              <CheckCircle weight="bold" className="w-4 h-4" />
                              Complete
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Blog Posts Tab */}
              {activeTab === "blog" && (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="blog-table">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="text-left p-4 font-semibold text-foreground">Title</th>
                        <th className="text-left p-4 font-semibold text-foreground">Category</th>
                        <th className="text-left p-4 font-semibold text-foreground">Status</th>
                        <th className="text-left p-4 font-semibold text-foreground">Views</th>
                        <th className="text-left p-4 font-semibold text-foreground">Date</th>
                        <th className="text-left p-4 font-semibold text-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterData(blogPosts).length === 0 ? (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-muted-foreground">
                            No blog posts found. Click "New Post" to create one.
                          </td>
                        </tr>
                      ) : (
                        filterData(blogPosts).map((post, i) => (
                          <tr key={i} className="border-b border-border hover:bg-muted/30 transition-colors">
                            <td className="p-4">
                              <div>
                                <p className="font-medium text-foreground">{post.title}</p>
                                <p className="text-xs text-muted-foreground">/blog/{post.slug}</p>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="px-2 py-1 bg-cyan-100 text-cyan-700 text-xs rounded-full capitalize">
                                {post.category}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                post.status === "published" 
                                  ? "bg-green-100 text-green-700" 
                                  : "bg-amber-100 text-amber-700"
                              }`}>
                                {post.status || "draft"}
                              </span>
                            </td>
                            <td className="p-4 text-muted-foreground">{post.views || 0}</td>
                            <td className="p-4 text-sm text-muted-foreground">{formatDate(post.created_at)}</td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openEditBlogPost(post)}
                                  className="w-8 h-8 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center hover:bg-cyan-200 transition-colors"
                                  title="Edit"
                                >
                                  <PencilSimple weight="bold" className="w-4 h-4" />
                                </button>
                                <a
                                  href={`/blog/${post.slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center hover:bg-slate-200 transition-colors"
                                  title="View"
                                >
                                  <Eye weight="bold" className="w-4 h-4" />
                                </a>
                                <button
                                  onClick={() => deleteBlogPost(post.id)}
                                  className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 transition-colors"
                                  title="Delete"
                                >
                                  <Trash weight="bold" className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Follow-up Modal */}
      {showFollowUpModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-6 text-white">
              <h2 className="text-xl font-bold">Create Follow-up Task</h2>
              <p className="text-white/80 text-sm mt-1">For: {selectedLead.name || selectedLead.email}</p>
            </div>
            <form onSubmit={createFollowUp} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Task Type</label>
                <select
                  className="form-select"
                  value={followUpForm.task_type}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, task_type: e.target.value })}
                >
                  <option value="call">Phone Call</option>
                  <option value="email">Email</option>
                  <option value="meeting">Meeting</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Due Date</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={followUpForm.due_date}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, due_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Notes (optional)</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  placeholder="Add any notes about this follow-up..."
                  value={followUpForm.notes}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, notes: e.target.value })}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowFollowUpModal(false);
                    setSelectedLead(null);
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Create Task
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
